import React, { useState, useEffect } from "react";
import {
  Modal,
  Alert,
  Checkbox,
  Form,
  Input,
  Radio,
  Card,
  Divider,
  Steps,
  Select,
  Tag,
  Image,
  Row,
  Col,
  List,
  Avatar,
  DatePicker,
  Dropdown,
  Button,
  Menu,
  Typography,
  Space,
  Badge,
  Spin,
} from "antd";
import "./ProductBooking.css";
import {
  SearchOutlined,
  PlusOutlined,
  MinusOutlined,
  ShoppingCartOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  DownOutlined,
} from "@ant-design/icons";
import moment from "moment";
import axios from "axios";
import { observer } from "mobx-react-lite";
import { useNavigate, useLocation, useParams } from "react-router-dom";

import { useAuth } from "../../../layouts/auth/AuthContext";
import { mstBooking, mstGeneral } from "../../../mobx";
import Navbar from "../../../layouts/navbar";
import { useProduct } from "./ProductContext";
import Paragraph from "antd/lib/typography/Paragraph";
import { useCart } from "../../../layouts/cart/CartContext";
import Footer from "../../../layouts/footer";
import { useReferral } from "../../../utils/referral";

const { Text, Link, Title } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;
const { Step } = Steps;
const { TextArea } = Input;

// Interface for variation selections
interface VariationSelection {
  variationId: string;
  variationTitle: string;
  selectedOptions: string[]; // array of option IDs
  selectionType: string;
  totalRequired: number;
}

// Interface for pickup selection
interface PickupSelection {
  pickupDate: string | null;
  pickupTime: string | null;
}

const ProductDetails = observer((props: any) => {
  const location = useLocation();
  const { id } = useParams();
  const { productDetails, fetchProductDetails, loading, addToCart } =
    useProduct();
  const { user } = useAuth();
  const { showCart } = useCart();
  const { navigateWithReferral } = useReferral();

  const [quantity, setQuantity] = useState(1);
  const [selectedVariations, setSelectedVariations] = useState<
    VariationSelection[]
  >([]);
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const [pickupSelection, setPickupSelection] = useState<PickupSelection>({
    pickupDate: null,
    pickupTime: null,
  });
  const [isPickupModalVisible, setIsPickupModalVisible] = useState(false);

  // Initialize variations when product details are loaded
  useEffect(() => {
    if (id) {
      fetchProductDetails(Number(id));
    }
  }, [id]);

  useEffect(() => {
    if (productDetails && productDetails.variations) {
      const initialSelections = productDetails.variations.map(
        (variation: any) => ({
          variationId: variation.id,
          variationTitle: variation.title,
          selectedOptions: [],
          selectionType: variation.selection_type,
          totalRequired: parseInt(variation.total_required) || 0,
        })
      );
      setSelectedVariations(initialSelections);
    }
  }, [productDetails]);

  // Generate available dates for the next 7 days
  const generateAvailableDates = () => {
    if (!productDetails) return [];

    const dates = [];
    const daysOfWeek = [
      "sunday",
      "monday",
      "tuesday",
      "wednesday",
      "thursday",
      "friday",
      "saturday",
    ];

    const today = moment();

    // Start from tomorrow (or today if same-day pickup is allowed)
    // For pickup, usually we start from the next available day
    const startFrom = today.add(1, "day"); // Start from tomorrow

    // Get the next 7 days starting from tomorrow
    for (let i = 0; i < 7; i++) {
      const date = startFrom.clone().add(i, "days");
      const dayName = daysOfWeek[date.day()];
      const availableKey = `${dayName}_available`;
      const collectionDateKey = `collection_${dayName}`;

      // Check if day is available
      // @ts-ignore
      const isAvailable = productDetails[availableKey] === "1";
      // @ts-ignore
      const collectionDateStr = productDetails[collectionDateKey];

      // Use the specific collection date from params if available
      let displayDate;
      let actualDate;

      if (collectionDateStr) {
        const collectionDate = moment(collectionDateStr);
        // Only use collection date if it's in the future
        if (collectionDate.isSameOrAfter(startFrom, "day")) {
          displayDate = collectionDate.format("D MMM");
          actualDate = collectionDateStr;
        } else {
          // If collection date is in the past, use the calculated date
          displayDate = date.format("D MMM");
          actualDate = date.format("YYYY-MM-DD");
        }
      } else {
        displayDate = date.format("D MMM");
        actualDate = date.format("YYYY-MM-DD");
      }

      dates.push({
        date: actualDate,
        displayDate: displayDate,
        dayName: date.format("ddd").toUpperCase(),
        isAvailable: isAvailable,
        collectionDate: collectionDateStr,
        moment: moment(actualDate),
      });
    }

    return dates;
  };

  // Generate time slots based on collection_time_start and collection_time_end
  const generateTimeSlots = () => {
    if (!productDetails || !pickupSelection.pickupDate) return [];

    const { collection_time_start, collection_time_end } = productDetails;

    if (!collection_time_start || !collection_time_end) return [];

    // Convert time strings to moment objects
    const startTime = moment(collection_time_start, "h:mm A");
    const endTime = moment(collection_time_end, "h:mm A");

    const timeSlots = [];
    const currentTime = startTime.clone();

    // Generate 30-minute intervals
    while (currentTime.isBefore(endTime)) {
      const timeSlot = currentTime.format("h:mm A");
      const nextSlot = currentTime.clone().add(30, "minutes").format("h:mm A");

      timeSlots.push({
        display: `${timeSlot} to ${nextSlot}`,
        value: timeSlot,
        startTime: timeSlot,
        endTime: nextSlot,
      });

      currentTime.add(30, "minutes");
    }

    return timeSlots;
  };

  // Handle pickup date selection
  const handleDateSelect = (date: any) => {
    setPickupSelection({
      pickupDate: date.date,
      pickupTime: null, // Reset time when date changes
    });
  };

  // Handle pickup time selection
  const handleTimeSelect = (timeSlot: string) => {
    setPickupSelection((prev) => ({
      ...prev,
      pickupTime: timeSlot,
    }));
  };

  // Show pickup selection modal
  const showPickupModal = () => {
    setIsPickupModalVisible(true);
  };

  // Handle modal OK
  const handleModalOk = () => {
    if (isPickupSelectionComplete()) {
      setIsPickupModalVisible(false);
    } else {
      Modal.warning({
        title: "Selection Required",
        content: "Please select both pickup date and time before proceeding.",
      });
    }
  };

  // Handle modal Cancel
  const handleModalCancel = () => {
    setIsPickupModalVisible(false);
  };

  // Format pickup selection for display
  const formatPickupSelection = () => {
    if (!pickupSelection.pickupDate || !pickupSelection.pickupTime) {
      return "Select pickup date & time";
    }

    const date = moment(pickupSelection.pickupDate);
    return `${date.format("ddd, D MMM")} at ${pickupSelection.pickupTime}`;
  };

  // Check if pickup selection is complete
  const isPickupSelectionComplete = () => {
    return pickupSelection.pickupDate && pickupSelection.pickupTime;
  };

  // Handle quantity changes
  const increaseQuantity = () => {
    setQuantity((prev) => prev + 1);
  };

  const decreaseQuantity = () => {
    if (quantity > 1) {
      setQuantity((prev) => prev - 1);
    }
  };

  // Handle variation selection
  const handleVariationSelection = (
    variationId: string,
    optionId: string,
    isSelected: boolean
  ) => {
    setSelectedVariations((prev) =>
      prev.map((variation) => {
        if (variation.variationId === variationId) {
          let newSelectedOptions = [...variation.selectedOptions];

          if (
            variation.selectionType === "optional" ||
            variation.selectionType === "required"
          ) {
            // For optional/required with total_required, handle multiple selections
            if (isSelected) {
              // Add option if not already selected and within totalRequired limit
              if (
                !newSelectedOptions.includes(optionId) &&
                (variation.totalRequired === 0 ||
                  newSelectedOptions.length < variation.totalRequired)
              ) {
                newSelectedOptions.push(optionId);
              }
            } else {
              // Remove option
              newSelectedOptions = newSelectedOptions.filter(
                (id) => id !== optionId
              );
            }
          } else {
            // For single selection (like radio behavior)
            newSelectedOptions = isSelected ? [optionId] : [];
          }

          return {
            ...variation,
            selectedOptions: newSelectedOptions,
          };
        }
        return variation;
      })
    );
  };

  // Check if a variation option is selected
  const isOptionSelected = (variationId: string, optionId: string): boolean => {
    const variation = selectedVariations.find(
      (v) => v.variationId === variationId
    );
    return variation ? variation.selectedOptions.includes(optionId) : false;
  };

  // Check if all required variations are satisfied
  const areRequiredVariationsSatisfied = (): boolean => {
    return selectedVariations.every((variation) => {
      if (variation.selectionType === "required") {
        return variation.selectedOptions.length >= variation.totalRequired;
      }
      return true; // Optional variations are always satisfied
    });
  };

  // Calculate total price including variations
  const calculateTotalPrice = (): number => {
    let total = parseFloat(productDetails?.price || "0");

    selectedVariations.forEach((variation) => {
      variation.selectedOptions.forEach((optionId) => {
        const variationObj = productDetails?.variations.find(
          (v: any) => v.id === variation.variationId
        );
        if (variationObj) {
          const option = variationObj.options.find(
            (opt: any) => opt.id === optionId
          );
          if (option) {
            total += parseFloat(option.price || "0");
          }
        }
      });
    });

    return total * quantity;
  };

  const calculatePricePerItem = (): number => {
    let total = parseFloat(productDetails?.price || "0");

    selectedVariations.forEach((variation) => {
      variation.selectedOptions.forEach((optionId) => {
        const variationObj = productDetails?.variations.find(
          (v: any) => v.id === variation.variationId
        );
        if (variationObj) {
          const option = variationObj.options.find(
            (opt: any) => opt.id === optionId
          );
          if (option) {
            total += parseFloat(option.price || "0");
          }
        }
      });
    });

    return total;
  };

  // Get selected variation details for display
  const getSelectedVariationsSummary = (): any[] => {
    const summary: any[] = [];

    selectedVariations.forEach((variation) => {
      if (variation.selectedOptions.length > 0) {
        const variationObj = productDetails?.variations.find(
          (v: any) => v.id === variation.variationId
        );
        if (variationObj) {
          variation.selectedOptions.forEach((optionId) => {
            const option = variationObj.options.find(
              (opt: any) => opt.id === optionId
            );
            if (option) {
              summary.push({
                optionId: option.id,
                variationTitle: variation.variationTitle,
                optionTitle: option.title,
                optionPrice: parseFloat(option.price || "0"),
                pickupdate: pickupSelection.pickupDate,
                pickuptime: pickupSelection.pickupTime,
              });
            }
          });
        }
      }
    });

    return summary;
  };

  // Add to cart function
  const handleAddToCart = async () => {
    if (!productDetails) return;

    // Check required variations
    if (!areRequiredVariationsSatisfied()) {
      Modal.warning({
        title: "Required Options",
        content: "Please select all required options before adding to cart.",
      });
      return;
    }

    // Check pickup selection
    if (!isPickupSelectionComplete()) {
      Modal.warning({
        title: "Pickup Selection Required",
        content:
          "Please select both pickup date and time before adding to cart.",
      });
      return;
    }

    setIsAddingToCart(true);

    try {
      // Create variant object for cart
      const variant = {
        price_id: productDetails.product_id, // Using product_id as base price_id
        title: productDetails.name,
        amount: calculateTotalPrice().toFixed(2),
        quantity: quantity,
        pricePerItem: calculatePricePerItem().toFixed(2),
        variations: getSelectedVariationsSummary(),
        pickupDate: pickupSelection.pickupDate,
        pickupTime: pickupSelection.pickupTime,
      };

      const newItem: any = {
        product_name: productDetails.name,
        product_id: productDetails.product_id,
        store_name: productDetails.store_name || "Restaurant",
        store_id: productDetails.store_id,
        variant: variant,
      };

      await addToCart(newItem);

      Modal.success({
        title: "Added to Cart",
        content: `${productDetails.name} has been added to your cart.`,
        okText: "View Cart",
        cancelText: "Continue Shopping",
        onOk: () => showCart(),
      });
    } catch (error) {
      Modal.error({
        title: "Error",
        content: "Failed to add product to cart. Please try again.",
      });
    } finally {
      setIsAddingToCart(false);
    }
  };

  // Render variation section based on selection type
  const renderVariationSection = (variation: any) => {
    const selection = selectedVariations.find(
      (v) => v.variationId === variation.id
    );

    if (!selection) return null;

    const getSelectionDescription = () => {
      if (variation.selection_type === "required") {
        if (variation.total_required > 0) {
          return `Required - Select ${variation.total_required} option(s)`;
        }
        return "Required - Select at least one option";
      } else {
        if (variation.total_required > 0) {
          return `Optional - Select up to ${variation.total_required} option(s)`;
        }
        return "Optional";
      }
    };

    return (
      <div key={variation.id} style={{ marginBottom: 24 }}>
        <div style={{ marginBottom: 8 }}>
          <Text strong>{variation.title}</Text>
          <Text type="secondary" style={{ marginLeft: 8, fontSize: 12 }}>
            {getSelectionDescription()}
          </Text>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {variation.options.map((option: any) => (
            <div
              key={option.id}
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "12px 16px",
                border: `1px solid ${
                  isOptionSelected(variation.id, option.id)
                    ? "#1890ff"
                    : "#d9d9d9"
                }`,
                borderRadius: 6,
                cursor: "pointer",
                backgroundColor: isOptionSelected(variation.id, option.id)
                  ? "#f0f8ff"
                  : "white",
                transition: "all 0.3s",
              }}
              onClick={() =>
                handleVariationSelection(
                  variation.id,
                  option.id,
                  !isOptionSelected(variation.id, option.id)
                )
              }
            >
              <div>
                <Text strong={isOptionSelected(variation.id, option.id)}>
                  {option.title}
                </Text>
              </div>
              <div>
                <Text type="secondary">
                  +RM {parseFloat(option.price || "0").toFixed(2)}
                </Text>
              </div>
            </div>
          ))}
        </div>

        {/* Show selection count for limited selections */}
        {variation.total_required > 0 && (
          <Text
            type="secondary"
            style={{ fontSize: 12, marginTop: 8, display: "block" }}
          >
            Selected: {selection.selectedOptions.length} /{" "}
            {variation.total_required}
          </Text>
        )}
      </div>
    );
  };

  // Render pickup date selection in modal
  const renderPickupDateSelection = () => {
    const availableDates = generateAvailableDates();

    return (
      <div style={{ marginBottom: 24 }}>
        <Title level={5}>
          <CalendarOutlined
            onPointerEnterCapture={undefined}
            onPointerLeaveCapture={undefined}
          />{" "}
          Select a Pickup Date
        </Title>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(5, 1fr)",
            gap: 8,
            marginBottom: 16,
          }}
        >
          {availableDates.slice(0, 5).map((date) => (
            <Card
              key={date.date}
              size="small"
              aria-disabled
              style={{
                textAlign: "center",
                cursor: date.isAvailable ? "pointer" : "not-allowed",
                borderColor:
                  pickupSelection.pickupDate === date.date
                    ? "#1890ff"
                    : "#d9d9d9",
                backgroundColor:
                  pickupSelection.pickupDate === date.date
                    ? "#f0f8ff"
                    : "white",
                opacity: date.isAvailable ? 1 : 0.5,
              }}
              onClick={() => date.isAvailable && handleDateSelect(date)}
            >
              <div style={{ fontSize: 12, fontWeight: "bold" }}>
                {date.dayName}
              </div>
              <div style={{ fontSize: 14, marginTop: 4 }}>
                {date.displayDate}
              </div>
              {/* {!date.isAvailable && (
                <div style={{ fontSize: 10, color: "#ff4d4f", marginTop: 4 }}>
                  Unavailable
                </div>
              )} */}
            </Card>
          ))}
        </div>
      </div>
    );
  };

  // Render pickup time selection in modal
  const renderPickupTimeSelection = () => {
    const timeSlots = generateTimeSlots();
    const selectedDate = generateAvailableDates().find(
      (date) => date.date === pickupSelection.pickupDate
    );

    if (!pickupSelection.pickupDate) {
      return (
        <div style={{ marginBottom: 24 }}>
          <Title level={5}>
            <ClockCircleOutlined
              onPointerEnterCapture={undefined}
              onPointerLeaveCapture={undefined}
            />{" "}
            Select a Pickup Time
          </Title>
          <div
            style={{
              padding: 16,
              textAlign: "center",
              background: "#f5f5f5",
              borderRadius: 6,
            }}
          >
            <Text type="secondary">Please select a pickup date first</Text>
          </div>
        </div>
      );
    }

    if (!selectedDate?.isAvailable) {
      return (
        <div style={{ marginBottom: 24 }}>
          <Title level={5}>
            <ClockCircleOutlined
              onPointerEnterCapture={undefined}
              onPointerLeaveCapture={undefined}
            />{" "}
            Select a Pickup Time
          </Title>
          <div
            style={{
              padding: 16,
              textAlign: "center",
              background: "#f5f5f5",
              borderRadius: 6,
            }}
          >
            <Text type="secondary">
              Selected date is not available for pickup
            </Text>
          </div>
        </div>
      );
    }

    if (timeSlots.length === 0) {
      return (
        <div style={{ marginBottom: 24 }}>
          <Title level={5}>
            <ClockCircleOutlined
              onPointerEnterCapture={undefined}
              onPointerLeaveCapture={undefined}
            />{" "}
            Select a Pickup Time
          </Title>
          <div
            style={{
              padding: 16,
              textAlign: "center",
              background: "#f5f5f5",
              borderRadius: 6,
            }}
          >
            <Text type="secondary">
              No time slots available for selected date
            </Text>
          </div>
        </div>
      );
    }

    return (
      <div style={{ marginBottom: 24 }}>
        <Title level={5}>
          <ClockCircleOutlined
            onPointerEnterCapture={undefined}
            onPointerLeaveCapture={undefined}
          />{" "}
          Select a Pickup Time
        </Title>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(2, 1fr)",
            gap: 8,
          }}
        >
          {timeSlots.map((slot) => (
            <div
              key={slot.value}
              style={{
                padding: "12px 8px",
                border: `1px solid ${
                  pickupSelection.pickupTime === slot.value
                    ? "#1890ff"
                    : "#d9d9d9"
                }`,
                borderRadius: 6,
                cursor: "pointer",
                backgroundColor:
                  pickupSelection.pickupTime === slot.value
                    ? "#f0f8ff"
                    : "white",
                transition: "all 0.3s",
                textAlign: "center",
              }}
              onClick={() => handleTimeSelect(slot.value)}
            >
              <Text strong={pickupSelection.pickupTime === slot.value}>
                {slot.display}
              </Text>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Render pickup selection modal
  const renderPickupModal = () => {
    return (
      // @ts-ignore
      <Modal
        title="Select Pickup Date & Time"
        visible={isPickupModalVisible}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
        width={600}
        okText="Confirm Selection"
        cancelText="Cancel"
      >
        {/* {isPickupSelectionComplete() && (
          <>
            <div
              style={{
                // marginBottom: 10,
                padding: 16,
                background: "#f0f8ff",
                borderRadius: 6,
                border: "1px solid #1890ff",
              }}
            >
              <Title level={5} style={{ marginBottom: 8 }}>
                Selected Pickup
              </Title>
              <Text strong>
                {moment(pickupSelection.pickupDate).format("dddd, D MMMM YYYY")}
              </Text>
              <br />
              <Text strong>Time: {pickupSelection.pickupTime}</Text>
            </div>
            <Divider />
          </>
        )} */}

        <div style={{ maxHeight: "60vh", overflowY: "auto" }}>
          {renderPickupDateSelection()}
          {renderPickupTimeSelection()}

          {/* Selected pickup summary */}
        </div>
      </Modal>
    );
  };

  if (loading) {
    return (
      <div id="HomeScreen">
        <div className="container">
          <Navbar hideBanner={true} />
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "50vh",
            }}
          >
            <Spin size="large" />
          </div>
          <Footer />
        </div>
      </div>
    );
  }

  if (!productDetails) {
    return (
      <div id="HomeScreen">
        <div className="container">
          <Navbar />
          <div style={{ textAlign: "center", padding: "40px" }}>
            <Title level={3}>Product Not Found</Title>
            <Button type="primary" onClick={() => navigateWithReferral(-1)}>
              Go Back
            </Button>
          </div>
          <Footer />
        </div>
      </div>
    );
  }

  const selectedVariationsSummary = getSelectedVariationsSummary();
  const basePrice = parseFloat(productDetails.price || "0");
  const totalPrice = calculateTotalPrice();

  return (
    <div id="HomeScreen">
      <div className="container">
        <Navbar hideBanner />

        {/* Back Button */}
        {/* <div style={{ marginBottom: 16 }}>
          <Button
            type="text"
            onClick={() => navigateWithReferral(-1)}
            style={{ paddingLeft: 0 }}
          >
            ← Back to Products
          </Button>
        </div> */}

        <Row gutter={[24, 24]}>
          {/* Product Image */}
          <Col xs={24} md={12}>
            <div
              style={{
                width: "100%",
                aspectRatio: "1 / 1",
                overflow: "hidden",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "#f5f5f5",
                borderRadius: "8px",
              }}
            >
              {productDetails.image_url ? (
                <img
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    objectPosition: "center",
                  }}
                  src={productDetails.image_url}
                  alt={productDetails.name}
                  onError={(e) => {
                    e.currentTarget.src = "/placeholder-image.png";
                  }}
                />
              ) : (
                <div style={{ textAlign: "center", color: "#999" }}>
                  <Image
                    width={100}
                    src="/placeholder-image.png"
                    preview={false}
                  />
                  <div>No Image Available</div>
                </div>
              )}
            </div>
          </Col>

          {/* Product Details */}
          <Col xs={24} md={12}>
            <div style={{ marginBottom: 16 }}>
              <Title level={2}>{productDetails.name}</Title>

              {/* Price Display */}
              <div style={{ marginBottom: 16 }}>
                <Text strong style={{ fontSize: 24, color: "#e90056" }}>
                  RM {basePrice.toFixed(2)}
                </Text>
                {productDetails.app_promotion_price &&
                  productDetails.app_promotion_price !== "0.00" && (
                    <div>
                      <Text
                        type="secondary"
                        style={{
                          textDecoration: "line-through",
                          marginRight: 8,
                        }}
                      >
                        RM {productDetails.price}
                      </Text>
                      <Text strong style={{ color: "#ff4d4f" }}>
                        Promo: RM {productDetails.app_promotion_price}
                      </Text>
                    </div>
                  )}
              </div>

              {/* Description */}
              {productDetails.desc && (
                <div style={{ marginBottom: 24 }}>
                  <Title level={5}>Description</Title>
                  <div
                    style={{
                      lineHeight: "1.6",
                      color: "#666",
                    }}
                    dangerouslySetInnerHTML={{ __html: productDetails.desc }}
                  />
                </div>
              )}

              {/* Category */}
              {productDetails.category &&
                productDetails.category.length > 0 && (
                  <div style={{ marginBottom: 16 }}>
                    <Text type="secondary">Category: </Text>
                    {productDetails.category.map((cat: any) => (
                      <Tag
                        key={cat.category_id}
                        color="blue"
                        style={{ margin: "2px" }}
                      >
                        {cat.category_name}
                      </Tag>
                    ))}
                  </div>
                )}

              <Divider />

              {/* Variations */}
              {productDetails.variations &&
                productDetails.variations.length > 0 && (
                  <>
                    <div style={{ marginBottom: 24 }}>
                      <Title level={5}>Customize Your Order</Title>
                      {productDetails.variations.map((variation: any) =>
                        renderVariationSection(variation)
                      )}
                    </div>
                    <Divider />
                  </>
                )}

              {/* Pickup Selection Button/Dropdown Style */}
              <div style={{ marginBottom: 24 }}>
                <Title level={5}>
                  <CalendarOutlined
                    onPointerEnterCapture={undefined}
                    onPointerLeaveCapture={undefined}
                  />{" "}
                  Pickup Date & Time
                </Title>
                <Button
                  type="default"
                  size="large"
                  icon={
                    <CalendarOutlined
                      onPointerEnterCapture={undefined}
                      onPointerLeaveCapture={undefined}
                    />
                  }
                  onClick={showPickupModal}
                  style={{
                    width: "100%",
                    height: 50,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    borderColor: isPickupSelectionComplete()
                      ? "#52c41a"
                      : "#d9d9d9",
                    backgroundColor: isPickupSelectionComplete()
                      ? "#f6ffed"
                      : "white",
                  }}
                >
                  <span
                    style={{
                      color: isPickupSelectionComplete() ? "#52c41a" : "#666",
                      fontWeight: isPickupSelectionComplete()
                        ? "bold"
                        : "normal",
                    }}
                  >
                    {formatPickupSelection()}
                  </span>
                  <DownOutlined
                    onPointerEnterCapture={undefined}
                    onPointerLeaveCapture={undefined}
                    style={{
                      color: isPickupSelectionComplete() ? "#52c41a" : "#666",
                    }}
                  />
                </Button>
                {!isPickupSelectionComplete() && (
                  <Text
                    type="secondary"
                    style={{
                      display: "block",
                      marginTop: 8,
                      fontSize: 12,
                    }}
                  >
                    ⓘ Required - Please select pickup date and time
                  </Text>
                )}
              </div>

              <Divider />

              {/* Quantity Selector */}
              <div style={{ marginBottom: 24 }}>
                <Title level={5}>Quantity</Title>
                <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                  {/* @ts-ignore */}
                  <Button.Group>
                    <Button
                      icon={
                        <MinusOutlined
                          onPointerEnterCapture={undefined}
                          onPointerLeaveCapture={undefined}
                        />
                      }
                      onClick={decreaseQuantity}
                      disabled={quantity <= 1}
                    />
                    <Button style={{ minWidth: 60 }}>{quantity}</Button>
                    <Button
                      icon={
                        <PlusOutlined
                          onPointerEnterCapture={undefined}
                          onPointerLeaveCapture={undefined}
                        />
                      }
                      onClick={increaseQuantity}
                    />
                  </Button.Group>
                  <Text type="secondary">{quantity} item(s)</Text>
                </div>
              </div>

              {/* Selected Options Summary */}
              {/* {selectedVariationsSummary.length > 0 && (
                <div
                  style={{
                    marginBottom: 24,
                    padding: 16,
                    background: "#f9f9f9",
                    borderRadius: 6,
                  }}
                >
                  <Title level={5}>Selected Options</Title>
                  {selectedVariationsSummary.map((item, index) => (
                    <div
                      key={index}
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        marginBottom: 4,
                      }}
                    >
                      <Text>
                        {item.variationTitle}: {item.optionTitle}
                      </Text>
                      <Text>+RM {item.optionPrice.toFixed(2)}</Text>
                    </div>
                  ))}
                </div>
              )} */}

              {/* Total Price and Add to Cart */}
              <div
                style={{
                  position: "sticky",
                  bottom: 0,
                  background: "white",
                  padding: "16px 0",
                  borderTop: "1px solid #f0f0f0",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: 16,
                  }}
                >
                  <div>
                    <Text strong style={{ fontSize: 18 }}>
                      Total: RM {totalPrice.toFixed(2)}
                    </Text>
                    {quantity > 1 && (
                      <div>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          (RM {(totalPrice / quantity).toFixed(2)} per item)
                        </Text>
                      </div>
                    )}
                  </div>
                </div>

                <Button
                  type="primary"
                  size="large"
                  icon={
                    <ShoppingCartOutlined
                      onPointerEnterCapture={undefined}
                      onPointerLeaveCapture={undefined}
                    />
                  }
                  loading={isAddingToCart}
                  onClick={handleAddToCart}
                  disabled={
                    !areRequiredVariationsSatisfied() ||
                    !isPickupSelectionComplete()
                  }
                  style={{
                    width: "100%",
                    height: 50,
                    fontSize: 16,
                    backgroundColor: "#e90056",
                    borderColor: "#e90056",
                    color: "#fff",
                  }}
                >
                  {isAddingToCart ? "Adding to Cart..." : "Add to Cart"}
                </Button>

                {!areRequiredVariationsSatisfied() && (
                  <Text
                    type="secondary"
                    style={{
                      display: "block",
                      marginTop: 8,
                      textAlign: "center",
                    }}
                  >
                    Please complete all required selections
                  </Text>
                )}

                {!isPickupSelectionComplete() &&
                  areRequiredVariationsSatisfied() && (
                    <Text
                      type="secondary"
                      style={{
                        display: "block",
                        marginTop: 8,
                        textAlign: "center",
                      }}
                    >
                      Please select pickup date and time
                    </Text>
                  )}
              </div>
            </div>
          </Col>
        </Row>

        {/* Pickup Selection Modal */}
        {renderPickupModal()}
        <Footer />
      </div>
    </div>
  );
});

export default ProductDetails;
